/**
 * 运营会话（C1）。★ 三条刻意的取舍：
 *   1) token 存 **sessionStorage** 而不是 localStorage：15m 的 admin JWT 关掉标签页就没了，
 *      共享电脑/浏览器配置备份里不会残留运营凭据；也躲开"跨站点重启后角色已变还拿着旧 token"。
 *      （不用 Cookie 是因为服务端走 Bearer + 密钥族分离，且 admin 域与 API 域不同源。）
 *   2) `role` 只用来隐藏入口（editor 看不见签发/退款/轮换 Token 的按钮），
 *      ★ 权限判定永远以服务端 requireAdmin(c,'admin') 为准 —— 前端隐藏只是省一次 403。
 *   3) 401 只触发一次跳转：后台一屏并发打 5 个接口，token 过期时它们同时失败，
 *      每个都触发跳转会把路由打成死循环。
 */
// ★ 用相对路径而不是 @/：让本模块在 Node（vitest environment:node）里也能直接 import，
//   一旦写成别名就必须在测试配置里再配一份解析规则（两个前端的 @ 指向不同目录，全局配不了）。

import { reactive } from 'vue';
import { type AdminClient, createAdminClient } from '../api/client';

export type AdminRole = 'admin' | 'editor';

const TOKEN_KEY = 'platform.admin.token';
const ROLE_KEY = 'platform.admin.role';

export interface AuthState {
  token: string | null;
  role: AdminRole | null;
  /** 会话过期标记：登录页显示"会话已过期"而不是一句莫名其妙的空白 */
  expired: boolean;
}

export const auth = reactive<AuthState>({
  token: sessionStorage.getItem(TOKEN_KEY),
  role: (sessionStorage.getItem(ROLE_KEY) as AdminRole | null) ?? null,
  expired: false,
});

function persist(token: string | null, role: AdminRole | null): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
  if (role) sessionStorage.setItem(ROLE_KEY, role);
  else sessionStorage.removeItem(ROLE_KEY);
}

/**
 * editor 无权的能力清单（与服务端 `minRole='admin'` 的路由一一对应）：
 * 新增 admin-only 接口时必须同步这里，否则按钮给了 editor，点了才收到 403。
 * 对应关系：promote→/admin/templates/:slug/promote、order.write→/admin/orders/:id/status、
 * refund→/admin/orders/:id/refund、license.write→/admin/licenses*、
 * github.write→/admin/integrations/github*、deploy.forceRetry→/admin/deployments/:id/force-retry
 */
const ADMIN_ONLY = new Set<AdminCapability>([
  'promote',
  'order.write',
  'refund',
  'license.write',
  'github.write',
  'deploy.forceRetry',
  'backend.write',
  'upgrade.write',
  // ★ C6 封禁/解封/重置密码、C10 配置读写、C13 翻译写入均 admin only（与服务端 requireAdmin('admin') 对齐）
  'user.write',
  'config.write',
  'i18n.write',
]);
export type AdminCapability =
  | 'promote'
  | 'order.write'
  | 'refund'
  | 'license.write'
  | 'github.write'
  | 'deploy.forceRetry'
  | 'backend.write'
  | 'upgrade.write'
  | 'user.write'
  | 'config.write'
  | 'i18n.write';

/** 只有 admin 角色放行；未登录（role=null）一律不放行，别把"没态"当成"有权限" */
export function can(cap: AdminCapability): boolean {
  return auth.role === 'admin' || (auth.role === 'editor' && !ADMIN_ONLY.has(cap));
}

/** 跳登录页的回调由 router.ts 注入（store 直接 import router 会形成 client→store→router→store 的环） */
let onForcedLogout: (() => void) | null = null;
export function setForcedLogoutHandler(fn: () => void): void {
  onForcedLogout = fn;
}

export const client: AdminClient = createAdminClient({
  token: () => auth.token,
  // ★ 并发去重已由 client 保证（一次跳一次），这里只负责清态 + 通知路由
  onUnauthorized: () => {
    persist(null, null);
    auth.token = null;
    auth.role = null;
    auth.expired = true;
    onForcedLogout?.();
  },
});

export async function login(email: string, password: string, totp?: string): Promise<void> {
  const s = await client.login(email, password, totp);
  persist(s.accessToken, s.role);
  auth.token = s.accessToken;
  auth.role = s.role;
  auth.expired = false;
}

export function logout(): void {
  // ★ 只做本地清除：运营侧 access token 不落库（15m 自然过期），
  //   真正的"立即吊销"要等迭代二的服务端会话表，这里绝不假装已吊销。
  persist(null, null);
  auth.token = null;
  auth.role = null;
}

export function isAuthed(): boolean {
  return auth.token !== null && auth.token.length > 0;
}
